// Teable Onboarding Helper — Revisr override
// Fixes field name mismatch: server expects `workspaceName`, crmfoundation sends `tenantName`

import { mainClient } from '@/utils/api/mainClient';

type CompleteOnboardingPayload = {
  workspaceName: string;
  workspaceDescription?: string;
  workspaceType?: string;
  profileData?: Record<string, unknown>;
  pendingInvites?: string[];
};

type CompleteOnboardingResponse = {
  success: boolean;
  tenantId?: string;
  message?: string;
  error?: string;
};

export const completeOnboarding = async (
  payload: CompleteOnboardingPayload
): Promise<CompleteOnboardingResponse> => {
  try {
    const { data } = await mainClient.post<CompleteOnboardingResponse>(
      '/api/onboarding',
      payload
    );
    return data;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Onboarding request failed',
    };
  }
};

type OnboardingStatus = {
  tenantId: string;
  spaceId: string | null;
  bases: Record<string, unknown>;
  isOnboarded: boolean;
};

const checkOnboardingStatus = async (
  tenantId: string
): Promise<OnboardingStatus | null> => {
  try {
    const { data } = await mainClient.get<OnboardingStatus>(
      `/api/onboarding/status/${tenantId}`
    );
    return data;
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return null;
  }
};

export const waitForOnboardingCompletion = async (
  tenantId: string,
  onProgress?: (status: OnboardingStatus) => void,
  maxWaitTime: number = 120000,
  pollInterval: number = 2000
): Promise<OnboardingStatus | null> => {
  const startTime = Date.now();

  return new Promise(resolve => {
    const poll = async () => {
      if (Date.now() - startTime > maxWaitTime) {
        console.warn('⚠️  Onboarding status check timed out');
        resolve(null);
        return;
      }

      const status = await checkOnboardingStatus(tenantId);

      if (status) {
        if (onProgress) onProgress(status);
        if (status.isOnboarded) {
          resolve(status);
          return;
        }
      }

      setTimeout(poll, pollInterval);
    };

    poll();
  });
};

// Teable onboarding is disabled — all data is stored in Supabase.
export const triggerTeableOnboarding = async (
  _tenantId: string,
  _tenantName: string,
  _waitForCompletion: boolean = false
): Promise<OnboardingStatus | null> => {
  return null;
};
